import { setCookie } from "cookies-next";

// external dependencies
import { useRouter } from "next/navigation";
import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useReCaptcha } from "next-recaptcha-v3";

// types
import { LoginCredentials } from "@/interface/auth.types";

import { COOKIE_CONFIG } from "@/config";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { IApiError } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import loginSchema from "@/schema/login";

// internal utilities and services
import { loginUser, verify2FA, resend2FA } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { parseApiError } from "@/lib/error-parser";

const useLogin = () => {
  const router = useRouter();
  const { executeRecaptcha, loaded: recaptchaLoaded } = useReCaptcha();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showTwoFAModal, setShowTwoFAModal] = useState<boolean>(false);
  const [twoFAEmail, setTwoFAEmail] = useState<string>("");
  const [currentLoginEmail, setCurrentLoginEmail] = useState<string>("");
  const [resendCountdown, setResendCountdown] = useState<number>(120); // 2 minutes
  const [resendDisabled, setResendDisabled] = useState<boolean>(true);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Countdown timer for resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendDisabled && resendCountdown > 0) {
      interval = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            setResendDisabled(false);
            return 120;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendDisabled, resendCountdown]);

  const { mutate: login, isPending: loginLoading } = useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      // Check if 2FA is required
      if (data.data.requires_2fa) {
        setTwoFAEmail(currentLoginEmail);
        setShowTwoFAModal(true);
        setResendDisabled(true);
        setResendCountdown(120);
        return;
      }

      // Normal login flow
      setCookie(COOKIE_CONFIG.loggedIn, "true", {
        maxAge: 30 * 24 * 60 * 60, // 30 days
        secure: process.env.PUBLIC_NODE_ENV === "production",
        sameSite: "lax",
      });
      showToast(TOAST_TYPES.success, data.message);
      router.push("/");
    },

    onError: (error: IApiError) => {
      console.error("Login error:", error);

      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  // 2FA verification mutation
  const { mutate: verifyTwoFA, isPending: isVerifying2FA } = useMutation({
    mutationFn: verify2FA,
    onSuccess: (data) => {
      setCookie(COOKIE_CONFIG.loggedIn, "true", {
        maxAge: 30 * 24 * 60 * 60, // 30 days
        secure: process.env.PUBLIC_NODE_ENV === "production",
        sameSite: "lax",
      });
      showToast(TOAST_TYPES.success, data.message);
      setShowTwoFAModal(false);
      router.push("/");
    },
    onError: (error: IApiError) => {
      parseApiError(error);
    },
  });

  // 2FA resend mutation
  const { mutate: resendTwoFA, isPending: isResending2FA } = useMutation({
    mutationFn: resend2FA,
    onSuccess: (data) => {
      showToast(TOAST_TYPES.success, data.message);
      setResendDisabled(true);
      setResendCountdown(120);
    },
    onError: (error: IApiError) => {
      console.error("2FA resend error:", error);
      const errors = error?.response?.data;
      showToast(
        TOAST_TYPES.error,
        errors?.message ?? "コードの再送信に失敗しました"
      );
    },
  });

  const handleVerify2FA = useCallback(
    (code: string) => {
      if (twoFAEmail) {
        verifyTwoFA({ email: twoFAEmail, code });
      }
    },
    [twoFAEmail, verifyTwoFA]
  );

  const handleResend2FA = useCallback(() => {
    if (twoFAEmail && !resendDisabled) {
      resendTwoFA({ email: twoFAEmail });
    }
  }, [twoFAEmail, resendDisabled, resendTwoFA]);

  const closeTwoFAModal = useCallback(() => {
    setShowTwoFAModal(false);
    setTwoFAEmail("");
    setCurrentLoginEmail("");
    setResendDisabled(true);
    setResendCountdown(120);
    // Clear any form errors when cancelling
    form.clearErrors();
  }, [form]);

  const onSubmit = useCallback(
    async (values: z.infer<typeof loginSchema>) => {
      if (!recaptchaLoaded) {
        showToast(
          TOAST_TYPES.error,
          "reCAPTCHAはまだ読み込み中です。再度お試しください。. Please try again."
        );
        return;
      }

      // Store the email for potential 2FA use
      setCurrentLoginEmail(values.email);

      try {
        // Generate reCAPTCHA token
        const token = await executeRecaptcha("login_form_submit");

        // Submit login with token
        const loginData: LoginCredentials = {
          grant_type: "password",
          email: values.email,
          password: values.password,
          captcha_v3_token: token,
          remember: true,
        };

        login(loginData);
      } catch (error) {
        console.error("reCAPTCHA error:", error);
        showToast(
          TOAST_TYPES.error,
          "reCAPTCHAの検証に失敗しました。再度お試しください。"
        );
      }
    },
    [executeRecaptcha, recaptchaLoaded, login]
  );

  return {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
    recaptchaLoaded,
    // 2FA related
    showTwoFAModal,
    twoFAEmail,
    isVerifying2FA,
    isResending2FA,
    resendDisabled,
    resendCountdown,
    handleVerify2FA,
    handleResend2FA,
    closeTwoFAModal,
  };
};

export default useLogin;
